import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQuery } from "react-query"
import { useDispatch, useSelector } from "react-redux"

import AddIcon from "@mui/icons-material/Add"
import { Grid } from "@mui/material"
import { Button, NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { ExpandableSection } from "../../components/UI/ExpandableSection"
import { SideBar } from "../../components/UI/SideBar"
import { KonanEmptyState } from "../../components/utility/KonanEmptyState"
import { SubHeader } from "../../layouts/SubHeader"
import { SidAPI } from "../../services/SidAPI"
import { queryClient } from "../../store/QueryClientStore"
import type { RootState } from "../../store/ReduxStore"
import type { CreateGroupRequestProps, CreateRoleRequestProps } from "../../types/custom/Permissions"
import type { GroupRetrieve } from "../../types/generated/authentication/GroupRetrieve"
import type { KonanResource } from "../../types/generated/authentication/KonanResource"
import type { Organization } from "../../types/generated/authentication/Organization"
import type { OrganizationInvitation } from "../../types/generated/authentication/OrganizationInvitation"
import type { Role } from "../../types/generated/authentication/Role"
import type { RolePermission } from "../../types/generated/authentication/RolePermission"
import { TypeEnum } from "../../types/generated/authentication/TypeEnum"
import type { UserManagementLog } from "../../types/generated/authentication/UserManagementLog"
import { useIsPermitted } from "../../utils/PermissionsHelpers"
import { GroupContainer, GroupCreationDialog, setHistory as setGroupsHistory, setgroups } from "../Groups"
import {
  Container,
  MemberCardLoader,
  MemberInviteDialog,
  PendingMemberContainer,
  setMembers,
  setPendingMembers,
} from "../Members"
import { RoleCardLoader, RoleContainer, RoleCreationDialog, setRoles, setHistory as setRolesHistory } from "../Roles"
import { LogItem } from "../Roles/LogItem"

interface SideBarSettingsReturn {
  title: string
  body?: React.ReactElement
  actions?: React.ReactElement[]
}

function GetSideBarSettings(mode: "pending-members" | "group-history" | "role-history" | null): SideBarSettingsReturn {
  // TODO:: Lookup a way to reduce this overhead
  // Idea:: make a separate function for each and call when needed
  const pendingInvites = useSelector((state: RootState) => state.members.pendingMembers)
  const rolesHistory = useSelector((state: RootState) => state.roles.history)
  const groupsHistory = useSelector((state: RootState) => state.groups.history)

  const { t } = useTranslation("settings")

  switch (mode) {
    case "pending-members":
      return {
        title: t("accessControl.sections.members.sidebarTitle"),
        body: (
          <Grid container item xs={12} spacing={1.5}>
            {(pendingInvites ?? []).length > 0 ? (
              pendingInvites.map((user) => (
                <Grid item xs={12} key={user.email}>
                  <PendingMemberContainer member={user} />
                </Grid>
              ))
            ) : (
              <Grid container item xs={12} display={"flex"} justifyContent={"center"} alignContent={"center"}>
                <KonanEmptyState title={t("accessControl.emptyStates.noInvites")} />
              </Grid>
            )}
          </Grid>
        ),
      }

    case "group-history":
      return {
        title: t("accessControl.sections.groups.sidebarTitle"),
        body: (
          <Grid container item xs={12} spacing={1.5}>
            {(groupsHistory ?? []).length > 0 ? (
              groupsHistory.map((log) => (
                <Grid item xs={12} key={log.uid}>
                  <LogItem log={log} />
                </Grid>
              ))
            ) : (
              <Grid container item xs={12} display={"flex"} justifyContent={"center"} alignContent={"center"}>
                <KonanEmptyState title={t("accessControl.emptyStates.noHistory")} />
              </Grid>
            )}
          </Grid>
        ),
      }

    case "role-history":
      return {
        title: t("accessControl.sections.roles.sidebarTitle"),
        body: (
          <Grid container item xs={12} spacing={1.5}>
            {(rolesHistory ?? []).length > 0 ? (
              rolesHistory.map((log) => (
                <Grid item xs={12} key={log.uid}>
                  <LogItem log={log} />
                </Grid>
              ))
            ) : (
              <Grid container item xs={12} display={"flex"} justifyContent={"center"} alignContent={"center"}>
                <KonanEmptyState title={t("accessControl.emptyStates.noHistory")} />
              </Grid>
            )}
          </Grid>
        ),
      }

    default:
      return {
        title: t("accessControl.emptyStates.error"),
      }
  }
}

/**
 * Wrapper component for Users/Team module
 * handles data fetching for `fetch-organization` endpoint
 * contains all related dialogs and Users table
 * @returns {React.ReactElement}
 */
export function RolesAndPermissions(): React.ReactElement {
  const dispatch = useDispatch()

  // extracting organization UID as well as the current user role
  const organizationUUID = useSelector((state: RootState) => state.auth.organizationId) as string
  const permissions = useSelector((state: RootState) => state.auth.permissions)

  const flattenedSidPermissions = useSelector((state: RootState) => state.permissions.flattenedSidPermissions)

  const { t } = useTranslation("settings")

  // Permission check constants
  const canListRoles = useIsPermitted("List roles", permissions.synapse_id, flattenedSidPermissions)
  const canListUsers = useIsPermitted("List users", permissions.synapse_id, flattenedSidPermissions)
  const canListGroups = useIsPermitted("List groups", permissions.synapse_id, flattenedSidPermissions)
  const canListInvitations = useIsPermitted("List user invitations", permissions.synapse_id, flattenedSidPermissions)
  const canInviteUsers = useIsPermitted("Invite users", permissions.synapse_id, flattenedSidPermissions)
  const canListLogs = useIsPermitted("List user management logs", permissions.synapse_id, flattenedSidPermissions)
  const canCreateRoles = useIsPermitted("Create roles", permissions.synapse_id, flattenedSidPermissions)
  const canCreateGroups = useIsPermitted("Create groups", permissions.synapse_id, flattenedSidPermissions)

  const [openInviteDialog, setOpenInviteDialog] = useState<boolean>(false)

  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState<boolean>(false)
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState<boolean>(false)

  const [isSideBarOpen, setIsSideBarOpen] = useState(false)
  const [drawerMode, setDrawerMode] = useState<"role-history" | "group-history" | "pending-members" | null>(null)

  const sideBarSettings = GetSideBarSettings(drawerMode)

  // Fetching organization roles
  const { isLoading: isRolesLoading, data: roles } = useQuery<AxiosResponse<Role[]>, AxiosError>(
    ["roles", organizationUUID],
    () => SidAPI.fetchRoles(organizationUUID),
    {
      onSuccess: (response) => {
        dispatch(setRoles(response.data))
      },
      enabled: !!organizationUUID && canListRoles,
      refetchOnMount: false,
    },
  )

  // Fetching organization groups
  const { isLoading: isGroupsLoading, data: groups } = useQuery<AxiosResponse<GroupRetrieve[]>, AxiosError>(
    ["groups", organizationUUID],
    () => SidAPI.fetchGroups(organizationUUID),
    {
      onSuccess: (response) => {
        dispatch(setgroups(response.data))
      },
      enabled: !!organizationUUID && canListGroups,
      refetchOnMount: false,
    },
  )

  // Fetching history
  const { isLoading: isHistoryLoading } = useQuery<AxiosResponse<UserManagementLog[]>, AxiosError>(
    ["change-history", organizationUUID],
    () => SidAPI.fetchUserManagementChangeHistory(organizationUUID),
    {
      onSuccess: (response) => {
        dispatch(setGroupsHistory(response.data.filter((log) => log.target.type === "group")))
        dispatch(setRolesHistory(response.data.filter((log) => log.target.type === "role")))
      },
      enabled: !!organizationUUID && canListLogs,
      refetchOnMount: false,
    },
  )

  // TODO: Handle what happens when errors appear here
  const { isLoading: isOrgFetching, data: OrganizationResponse } = useQuery<AxiosResponse<Organization>, AxiosError>(
    ["Organization", organizationUUID],
    () => SidAPI.fetchOrganization(organizationUUID),
    {
      onSuccess: (response) => {
        dispatch(setMembers(response.data.users))
      },
      enabled: !!organizationUUID,
      refetchOnMount: false,
    },
  )

  // Fetching organization invitations
  const { isLoading: isPendingInvitesLoading } = useQuery<AxiosResponse<Array<OrganizationInvitation>>, AxiosError>(
    ["OrganizationInvitation", organizationUUID],
    () => SidAPI.fetchPendingInvites(organizationUUID),
    {
      onSuccess: (response) => {
        dispatch(setPendingMembers(response.data))
      },
      enabled: canListInvitations,
      refetchOnMount: false,
    },
  )

  const CreateRoleMutation = useMutation<
    AxiosResponse<Role>,
    AxiosError,
    CreateRoleRequestProps & { organization_uuid: string }
  >(SidAPI.createRole, {
    onSuccess: () => {
      NotificationUtils.toast(t("accessControl.notifications.createRole.success"), {
        severity: "success",
      })

      queryClient.invalidateQueries("konan-permissions")
      queryClient.invalidateQueries("sid-permissions")
      queryClient.invalidateQueries(["roles", organizationUUID])
    },
    onError: () =>
      NotificationUtils.toast(t("accessControl.notifications.createRole.error"), {
        severity: "error",
      }),
  })

  const createRoleHandler = async (name: string, description: string, permissions: RolePermission[]): Promise<void> => {
    await CreateRoleMutation.mutateAsync({
      organization_uuid: organizationUUID,
      name: name,
      description: description,
      permissions: permissions,
    })

    setIsRoleDialogOpen(false)
  }

  const CreateGroupMutation = useMutation<
    AxiosResponse<GroupRetrieve>,
    AxiosError,
    CreateGroupRequestProps & { organization_uuid: string }
  >(SidAPI.createGroup, {
    onSuccess: () => {
      NotificationUtils.toast(t("accessControl.notifications.createGroup.success"), {
        severity: "success",
      })

      queryClient.invalidateQueries(["groups", organizationUUID])
    },
    onError: () =>
      NotificationUtils.toast(t("accessControl.notifications.createGroup.error"), {
        severity: "error",
      }),
  })

  const createGroupHandler = async (name: string, resources: KonanResource[]): Promise<void> => {
    await CreateGroupMutation.mutateAsync({
      organization_uuid: organizationUUID,
      name: name,
      konan_resources: [
        ...resources.map((resource): KonanResource => {
          return { uid: resource.uid, type: TypeEnum.PROJECT }
        }),
      ],
    })
  }

  return (
    <React.Fragment>
      {/* Dialogs*/}
      <MemberInviteDialog isOpen={openInviteDialog} onClose={() => setOpenInviteDialog(false)} />

      <RoleCreationDialog
        isOpen={isRoleDialogOpen}
        onClose={() => setIsRoleDialogOpen(false)}
        onAccept={createRoleHandler}
      />

      <GroupCreationDialog
        isOpen={isGroupDialogOpen}
        onClose={() => setIsGroupDialogOpen(false)}
        onAccept={createGroupHandler}
      />

      <Grid container item xs={12} display={"block"} wrap="nowrap">
        <Grid container item xs={12}>
          {canListUsers && (
            <Grid container item xs={12} mb={1}>
              <ExpandableSection
                open
                headerContent={
                  <SubHeader
                    title={t("accessControl.sections.members.title")}
                    actions={[
                      canListInvitations ? (
                        <Button
                          id="sent-invites"
                          key="sent-invites"
                          variant="secondary"
                          size="regular"
                          onClick={() => {
                            setIsSideBarOpen(true)
                            setDrawerMode("pending-members")
                          }}
                          disabled={isPendingInvitesLoading}
                        >
                          {t("accessControl.sections.members.pendingMembers")}
                        </Button>
                      ) : undefined,
                      canInviteUsers ? (
                        <Button
                          size="regular"
                          id="invite-members"
                          key="invite-members"
                          variant="primary-inverse"
                          onClick={() => setOpenInviteDialog(true)}
                          startIcon={<AddIcon fontSize="small" />}
                        >
                          {t("accessControl.sections.members.inviteMembers")}
                        </Button>
                      ) : undefined,
                    ]}
                  />
                }
              >
                <Grid container item xs={12}>
                  {isOrgFetching && (
                    <Grid container item spacing={1.5}>
                      {[1, 2, 3].map((item: number) => {
                        return (
                          <Grid item xs={12} md={6} lg={3} key={`member-loader-${item}`}>
                            <MemberCardLoader />
                          </Grid>
                        )
                      })}
                    </Grid>
                  )}
                  {!isOrgFetching && (
                    <Grid container item spacing={1.5}>
                      {[...(OrganizationResponse?.data.users ?? [])]
                        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                        .map((user) => (
                          <Grid item xs={12} md={6} lg={3} key={user.email}>
                            <Container member={user} isLastMember={OrganizationResponse?.data.users.length === 1} />
                          </Grid>
                        ))}
                    </Grid>
                  )}
                </Grid>
              </ExpandableSection>
            </Grid>
          )}

          {canListRoles && (
            <Fragment>
              <Grid container item xs={12} py={1}>
                <ExpandableSection
                  headerContent={
                    <SubHeader
                      title={t("accessControl.sections.roles.title")}
                      actions={[
                        canListLogs ? (
                          <Button
                            variant="secondary"
                            onClick={() => {
                              setIsSideBarOpen(true)
                              setDrawerMode("role-history")
                            }}
                            disabled={isHistoryLoading}
                            size="regular"
                          >
                            {t("accessControl.sections.roles.history")}
                          </Button>
                        ) : undefined,
                        canCreateRoles ? (
                          <Button
                            key="sent-invites"
                            id="sent-invites"
                            variant="primary-inverse"
                            onClick={() => setIsRoleDialogOpen(true)}
                            startIcon={<AddIcon fontSize="small" />}
                            size="regular"
                          >
                            {t("accessControl.sections.roles.newRole")}
                          </Button>
                        ) : undefined,
                      ]}
                    />
                  }
                >
                  <Grid container item xs={12}>
                    {isRolesLoading && (
                      <Grid container item spacing={1.5}>
                        {[1, 2, 3].map((item: number) => {
                          return (
                            <Grid item xs={12} md={6} lg={3} key={`role-loader-${item}`}>
                              <RoleCardLoader />
                            </Grid>
                          )
                        })}
                      </Grid>
                    )}

                    {!isRolesLoading && (roles?.data ?? []).length > 0 && (
                      <Grid container item spacing={1.5}>
                        {[...(roles?.data ?? [])]
                          ?.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                          ?.sort((a, _) => (a.is_owner ? -1 : 0))
                          .map((role) => (
                            <Grid item xs={12} md={6} lg={3} key={role.name}>
                              <RoleContainer role={role} users={role.users.map((user) => user.email)} />
                            </Grid>
                          ))}
                      </Grid>
                    )}
                  </Grid>
                </ExpandableSection>
              </Grid>
            </Fragment>
          )}

          {canListGroups && (
            <Fragment>
              <Grid container item xs={12} pt={1}>
                <ExpandableSection
                  headerContent={
                    <SubHeader
                      title={t("accessControl.sections.groups.title")}
                      actions={[
                        canListLogs ? (
                          <Button
                            variant="secondary"
                            onClick={() => {
                              setIsSideBarOpen(true)
                              setDrawerMode("group-history")
                            }}
                            disabled={isHistoryLoading}
                            size="regular"
                          >
                            {t("accessControl.sections.groups.history")}
                          </Button>
                        ) : undefined,
                        canCreateGroups ? (
                          <Button
                            key="sent-invites"
                            id="sent-invites"
                            variant="primary-inverse"
                            onClick={() => setIsGroupDialogOpen(true)}
                            startIcon={<AddIcon fontSize="small" />}
                            size="regular"
                          >
                            {t("accessControl.sections.groups.newGroup")}
                          </Button>
                        ) : undefined,
                      ]}
                    />
                  }
                >
                  <Fragment>
                    {isGroupsLoading && (
                      <Grid container item spacing={1.5}>
                        {[1, 2, 3].map((item: number) => {
                          return (
                            <Grid item xs={12} md={6} lg={3} key={`group-loader-${item}`}>
                              <RoleCardLoader />
                            </Grid>
                          )
                        })}
                      </Grid>
                    )}

                    {!isGroupsLoading && (groups?.data ?? []).length > 0 && (
                      <Grid container item spacing={1.5}>
                        {[...(groups?.data ?? [])]
                          ?.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                          .map((group) => (
                            <Grid item xs={12} md={6} lg={3} key={group.name}>
                              <GroupContainer
                                group={group}
                                immutable={group.name === "All Projects"}
                                users={group.users.map((user) => user.email)}
                              />
                            </Grid>
                          ))}
                      </Grid>
                    )}
                  </Fragment>
                </ExpandableSection>
              </Grid>
            </Fragment>
          )}
        </Grid>

        <SideBar
          isOpen={isSideBarOpen}
          title={sideBarSettings.title}
          onClose={() => setIsSideBarOpen(false)}
          actions={sideBarSettings.actions}
          variant="floating"
          closeIcon={{
            icon: "close",
            position: "right",
          }}
        >
          {sideBarSettings.body}
        </SideBar>
      </Grid>
    </React.Fragment>
  )
}
